import type { DrawingGameNotice, MacaoSnapshot } from "@aproape/shared";

export function shouldPlayChatSound(senderId: string, selfSessionId: string): boolean {
  return selfSessionId.length > 0 && senderId !== selfSessionId;
}

export type DrawingSoundCue = "guess" | "success" | "roundStart" | "hint" | "roundEnd" | "matchEnd" | "error";

export function soundCueForDrawingNotice(type: DrawingGameNotice["type"]): DrawingSoundCue {
  if (type === "guess_submitted") return "guess";
  if (type === "player_guessed") return "success";
  if (type === "round_started") return "roundStart";
  if (type === "hint_revealed") return "hint";
  if (type === "round_ended") return "roundEnd";
  if (type === "match_ended") return "matchEnd";
  return "error";
}

type SoundKey = "chat" | "typing" | "drawing" | "ui" | DrawingSoundCue;
export type MacaoSoundCue = "cardPlay" | "cardDraw" | "turn" | "macaoWin" | "invalidCard";

export function macaoSoundCues(previous: MacaoSnapshot | null, next: MacaoSnapshot, currentUserId: string): MacaoSoundCue[] {
  if (!previous) return [];
  const cues: MacaoSoundCue[] = [];
  if (previous.topCard?.id !== next.topCard?.id) cues.push("cardPlay");
  const previousCounts = new Map(previous.players.map((player) => [player.userId, player.cardCount]));
  if (next.players.some((player) => player.cardCount > (previousCounts.get(player.userId) ?? player.cardCount))) cues.push("cardDraw");
  if (previous.currentPlayerUserId !== next.currentPlayerUserId && next.currentPlayerUserId === currentUserId && next.session.status === "playing") cues.push("turn");
  if (previous.session.status !== "finished" && next.session.status === "finished") cues.push("macaoWin");
  return cues;
}

type AnySoundKey = SoundKey | MacaoSoundCue;

export class ChatSound {
  private context?: AudioContext;
  private readonly lastPlayed = new Map<AnySoundKey, number>();

  async unlock(): Promise<void> {
    const AudioContextConstructor = window.AudioContext
      ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return;
    try {
      this.context ??= new AudioContextConstructor();
      if (this.context.state === "suspended") await this.context.resume();
    } catch {
      // Some browsers keep audio locked until a later user interaction.
    }
  }

  play(): void { this.playChat(); }

  playChat(): void {
    if (!this.available("chat", 120)) return;
    this.notes([620, 880], .18, .045, "sine");
  }

  playTyping(): void {
    if (!this.available("typing", 650)) return;
    this.notes([760, 980], .12, .028, "sine");
  }

  playDrawing(): void {
    if (!this.available("drawing", 72)) return;
    const context = this.context;
    if (!context || context.state !== "running") return;
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sawtooth";
    oscillator.frequency.setValueAtTime(105 + Math.random() * 55, now);
    oscillator.frequency.linearRampToValueAtTime(75 + Math.random() * 35, now + .055);
    gain.gain.setValueAtTime(.0001, now);
    gain.gain.exponentialRampToValueAtTime(.012, now + .006);
    gain.gain.exponentialRampToValueAtTime(.0001, now + .065);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + .07);
  }

  playUi(): void {
    if (!this.available("ui", 90)) return;
    this.notes([390, 520], .1, .025, "sine");
  }

  playDrawingNotice(type: DrawingGameNotice["type"]): void {
    this.playCue(soundCueForDrawingNotice(type));
  }

  playMacao(cue: MacaoSoundCue): void {
    if (!this.available(cue, cue === "turn" ? 250 : 100)) return;
    if (cue === "cardPlay") this.notes([240, 390], .11, .024, "triangle");
    else if (cue === "cardDraw") this.notes([180, 250, 330], .14, .019, "sine");
    else if (cue === "turn") this.notes([570, 760], .16, .028, "sine");
    else if (cue === "macaoWin") this.notes([520, 690, 870, 1_080], .42, .048, "triangle");
    else this.notes([210, 160], .15, .03, "square");
  }

  playCue(cue: DrawingSoundCue): void {
    if (!this.available(cue, 180)) return;
    if (cue === "guess") this.notes([480], .08, .018, "sine");
    else if (cue === "success") this.notes([660, 880, 1_050], .26, .045, "sine");
    else if (cue === "roundStart") this.notes([420, 610, 820], .3, .04, "triangle");
    else if (cue === "hint") this.notes([520, 690], .22, .034, "sine");
    else if (cue === "roundEnd") this.notes([700, 520], .22, .035, "triangle");
    else if (cue === "matchEnd") this.notes([520, 680, 860, 1_040], .42, .048, "triangle");
    else this.notes([240, 190], .16, .026, "triangle");
  }

  destroy(): void {
    const context = this.context;
    this.context = undefined;
    this.lastPlayed.clear();
    if (context && context.state !== "closed") void context.close();
  }

  private available(key: AnySoundKey, cooldownMs: number): boolean {
    const context = this.context;
    if (!context || context.state !== "running") return false;
    const now = Date.now();
    const last = this.lastPlayed.get(key) ?? 0;
    if (now - last < cooldownMs) return false;
    this.lastPlayed.set(key, now);
    return true;
  }

  private notes(frequencies: number[], duration: number, volume: number, type: OscillatorType): void {
    const context = this.context;
    if (!context || context.state !== "running") return;
    const noteDuration = duration / frequencies.length;
    frequencies.forEach((frequency, index) => {
      const start = context.currentTime + index * noteDuration;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(.0001, start);
      gain.gain.exponentialRampToValueAtTime(volume, start + .008);
      gain.gain.exponentialRampToValueAtTime(.0001, start + noteDuration * .92);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + noteDuration);
    });
  }
}
